import { useCallback, useEffect, useState } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { toast } from "sonner";
import {
    driveStatus,
    driveConnect,
    driveDisconnect,
    driveRunNow,
} from "../lib/api";
import { isTauri, openDriveConnect, onDriveCode, DRIVE_REDIRECT } from "../lib/native";

function formatTs(iso) {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

export default function DriveConnect() {
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const s = await driveStatus();
            setStatus(s);
        } catch (e) {
            // Stay quiet on first load if backend hasn't been hit yet
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Desktop: the auth code comes back via the lisn://drive deep link (OAuth
    // can't run inside the macOS webview). Exchange it with the matching
    // redirect_uri so the backend's token exchange succeeds.
    useEffect(() => {
        if (!isTauri()) return undefined;
        let un = () => {};
        (async () => {
            un = await onDriveCode(async (code) => {
                setBusy(true);
                try {
                    await driveConnect(code, DRIVE_REDIRECT);
                    toast.success("Drive backup connected");
                    await refresh();
                } catch (e) {
                    toast.error(e?.response?.data?.detail || e.message || "Connect failed");
                } finally {
                    setBusy(false);
                }
            });
        })();
        return () => { try { un(); } catch { /* ignore */ } };
    }, [refresh]);

    const login = useGoogleLogin({
        flow: "auth-code",
        scope: "https://www.googleapis.com/auth/drive.file",
        // Ask for offline access so Google returns a refresh_token, and force
        // the consent screen so we don't get a token-less response on re-grant.
        access_type: "offline",
        prompt: "consent",
        onSuccess: async (resp) => {
            if (!resp.code) {
                toast.error("Drive: no auth code returned");
                return;
            }
            setBusy(true);
            try {
                await driveConnect(resp.code);
                toast.success("Drive backup connected");
                await refresh();
            } catch (e) {
                const msg = e?.response?.data?.detail || e.message || "Connect failed";
                toast.error(msg);
            } finally {
                setBusy(false);
            }
        },
        onError: () => toast.error("Google Drive sign-in failed"),
    });

    async function disconnect() {
        setBusy(true);
        try {
            await driveDisconnect();
            toast.success("Drive backup disconnected");
            await refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Disconnect failed");
        } finally {
            setBusy(false);
        }
    }

    async function runNow() {
        setBusy(true);
        try {
            const r = await driveRunNow();
            toast.success(`Backed up ${r.uploaded} session${r.uploaded === 1 ? "" : "s"}`);
            await refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Backup failed");
        } finally {
            setBusy(false);
        }
    }

    if (!status) {
        return null;
    }

    return (
        <div
            className="rounded-[20px] border border-[#EFEFEF] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.03)] p-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6"
            data-testid="drive-connect"
        >
            <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-[15px] tracking-tight text-zinc-900">Google Drive backup</div>
                {status.connected ? (
                    <div className="text-[13px] text-[#52525B] mt-1 leading-relaxed">
                        Connected. Completed sessions back up nightly at 02:00 UTC to your{" "}
                        {status.folder_link ? (
                            <a
                                href={status.folder_link}
                                target="_blank"
                                rel="noreferrer"
                                className="underline hover:text-black"
                            >
                                Lisn Backups folder
                            </a>
                        ) : (
                            <span>Lisn Backups folder</span>
                        )}
                        .
                        {status.last_backup_at && (
                            <span>
                                {" "}Last run: {formatTs(status.last_backup_at)}
                                {typeof status.last_backup_count === "number"
                                    ? ` (${status.last_backup_count} uploaded)`
                                    : ""}.
                            </span>
                        )}
                    </div>
                ) : (
                    <div className="text-[13px] text-[#52525B] mt-1 leading-relaxed">
                        Back up completed sessions to your Google Drive — JSON files saved
                        nightly to a private folder. Lisn can only see what it creates.
                    </div>
                )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {status.connected ? (
                    <>
                        <button
                            onClick={runNow}
                            disabled={busy}
                            className="text-[13px] font-semibold px-5 h-10 rounded-full bg-[#0A0A0A] hover:bg-black text-white shadow-[0_1px_2px_rgba(0,0,0,0.06)] disabled:opacity-50"
                            data-testid="drive-run-now"
                        >
                            {busy ? "Backing up…" : "Back up now"}
                        </button>
                        <button
                            onClick={disconnect}
                            disabled={busy}
                            className="text-[13px] font-medium px-4 h-10 rounded-full border border-[#EFEFEF] hover:border-[#D4D4D8] text-zinc-700 disabled:opacity-50"
                            data-testid="drive-disconnect"
                        >
                            Disconnect
                        </button>
                    </>
                ) : (
                    <button
                        onClick={() => (isTauri() ? openDriveConnect() : login())}
                        disabled={busy}
                        className="text-[13px] font-semibold px-5 h-10 rounded-full bg-[#0A0A0A] hover:bg-black text-white shadow-[0_1px_2px_rgba(0,0,0,0.06)] disabled:opacity-50"
                        data-testid="drive-connect-button"
                    >
                        {busy ? "Connecting…" : "Connect Google Drive"}
                    </button>
                )}
            </div>
        </div>
    );
}
